import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Auth } from "@gss/common/Auth";
import { sweCompare } from "@gss/common/sweCompare";
import { useMessages } from "@gss/components/Messages";
import type { MeasurementStat } from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import { DAO } from "../DAO";
import type { Option } from "../export/export.types";
import { isAllOption, toFilterParam } from "../filters/FilterUtil";
import type { UserOrganisations } from "../types";
import { DownloadAggregated } from "./DownloadAggregated";
import { DownloadFilters } from "./DownloadFilters";
import { DownloadQuestionnaire } from "./DownloadQuestionnaire";
import "./DownloadsResults.css";
import { DownloadRaw } from "./DownloadRaw";
import { DownloadStandardReport } from "./DownloadStandardReport";

interface Props {
    stat: MeasurementStat;
    audience: SecurityContext;
    userOrganistions: UserOrganisations;
}

export function DownloadResultsTab({
    stat,
    audience,
    userOrganistions,
}: Props): JSX.Element {
    const [rawRegions, setRawRegions] = useState<Option[]>([]);
    const [filterValues, setFilterValues] = useState<Record<string, string>>(
        {},
    );
    const { addError } = useMessages();
    const isReader = audience === "authenticated" && Auth.isReader();

    useEffect(() => {
        if (isReader) {
            DAO.export
                .getRawExportRegions(stat.measurement, stat.periods)
                .then((regions) =>
                    setRawRegions(
                        regions.toSorted((a, b) => sweCompare(a.name, b.name)),
                    ),
                )
                .catch(addError);
        }
    }, []);

    const filterParams = Object.entries(filterValues)
        .filter(([_, optionValue]) => !isAllOption(optionValue))
        .map(([filterValue, optionValue]) =>
            toFilterParam(filterValue, optionValue),
        );

    /* ---------- AUTHENTICATED (READER) AUDIENCE ---------- */

    const renderFilters = () => {
        if (!isReader) {
            return null;
        }
        return (
            <DownloadFilters
                stat={stat}
                values={filterValues}
                onChange={(filterValue, optionValue) => {
                    setFilterValues({
                        ...filterValues,
                        [filterValue]: optionValue,
                    });
                }}
            />
        );
    };

    /* ---------- PUBLIC AUDIENCE ---------- */

    const renderAggregated = () => {
        return (
            <DownloadAggregated
                stat={stat}
                audience={audience}
                filterParams={filterParams}
            />
        );
    };

    /* ---------- AUTHENTICATED (READER) AUDIENCE ---------- */

    const renderStandardReport = () => {
        if (!isReader) {
            return null;
        }
        return (
            <>
                <hr />
                <DownloadStandardReport
                    stat={stat}
                    userOrganistions={userOrganistions}
                    filterParams={filterParams}
                />
            </>
        );
    };

    const renderQuestionnaire = () => {
        if (!isReader) {
            return null;
        }
        return (
            <>
                <hr />
                <DownloadQuestionnaire stat={stat} />
            </>
        );
    };

    const renderRawRegions = () => {
        // isReader check is done in useEffect when fetching raw regions
        if (rawRegions.length === 0) {
            return null;
        }
        return (
            <>
                <hr />
                <DownloadRaw stat={stat} regions={rawRegions} />
            </>
        );
    };

    return (
        <section className="mb-3">
            {renderFilters()}
            {renderAggregated()}
            {renderStandardReport()}
            {renderQuestionnaire()}
            {renderRawRegions()}
        </section>
    );
}
